import dayjs from 'dayjs';
import uniqBy from 'lodash/uniqBy';

import { DateMessagesProps, MessageProps } from 'contexts/messagesContext/messagesContext.types';

/**
 * Groups chat messages by day and sorts both the buckets and messages chronologically.
 *
 * @param messages - Array of messages that include a sent timestamp.
 * @returns Array of day buckets with messages ordered by send time.
 */
export const groupMessagesByDate = (messages: MessageProps[]): DateMessagesProps[] => {
  const buckets = new Map<string, { messages: MessageProps[]; sortKey: number }>();

  messages.forEach((message) => {
    const sentAt = dayjs(message.messageStatus.sent);
    const bucketDate = sentAt.toDate().toDateString();
    const existingBucket = buckets.get(bucketDate);

    if (existingBucket) {
      existingBucket.messages.push(message);
      return;
    }

    buckets.set(bucketDate, {
      messages: [message],
      sortKey: sentAt.startOf('day').valueOf()
    });
  });

  return [...buckets.entries()]
    .sort(([, a], [, b]) => a.sortKey - b.sortKey)
    .map(([date, { messages: dayMessages }]) => ({
      date,
      messages: dayMessages.sort((a, b) => dayjs(a.messageStatus.sent).diff(b.messageStatus.sent))
    }));
};

/**
 * Merges existing channels with incoming updates, preserving order and uniqueness by channelId.
 *
 * @param current - Existing list of channels.
 * @param incoming - New channels to merge into the existing list.
 * @returns Deduplicated list of channels keyed by channelId.
 */
export const mergeChannelsById = <T extends { channelId: string }>(
  current: T[],
  incoming: T[]
): T[] => uniqBy([...current, ...incoming], 'channelId');

/**
 * Updates channel metadata inside a channel collection using a partial update payload.
 *
 * @param channels - Current channel list.
 * @param update - Partial channel fields keyed by channelId.
 * @returns Updated channel list with the matching channel patched.
 */
export const updateChannelInList = <T extends { channelId: string }>(
  channels: T[],
  update: Partial<T> & { channelId: string }
): T[] => {
  const channelIndex = channels.findIndex((channel) => channel.channelId === update.channelId);
  if (channelIndex === -1) return channels;

  const nextChannels = [...channels];
  nextChannels[channelIndex] = {
    ...nextChannels[channelIndex],
    ...update
  };

  return nextChannels;
};
